/**
 * <SalesTrendChart trend={hub.trend} metric="revenue" onMetric={setMetric} compareLabel="vs previous 30 days"
 *                  onSelect={bucket => ...} loading={loading} />
 *
 * Main overview chart (recharts ComposedChart): bars = selected period, dashed line = comparison period
 * (aligned by day offset). Daily bars; ranges over 45 days are summed into 7-day buckets.
 *   metric      'revenue' | 'units' | 'returns' (the Revenue/Units/Returns KPI cards also switch it)
 *   onSelect    called with the clicked bucket { from, to, ... } (the page opens /daily-sales for it)
 * The tooltip shows the date, value, comparison value and the change (₹ and %); dates carry the year
 * when the comparison is in another year (compare=last_year) so the two rows can be told apart.
 * Keyboard / screen-reader drill-down: a "Open daily records" list of one button per bar (visually
 * hidden until a button gets focus) calls onSelect like a click on the bar.
 */
import React, { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Skeleton } from '../ui/skeleton';
import { DeltaBadge } from './DeltaBadge';
import { number, shortMoney, wholeMoney } from './format';
import { bucketLabel, bucketTrend, bucketYearsDiffer, previousBucketLabel, shouldBucketWeekly } from './overview/trend';

export const METRIC_META = {
  revenue: { label: 'Revenue', color: '#2563EB', kind: 'money', short: shortMoney, full: wholeMoney },
  units: { label: 'Units', color: '#059669', kind: 'number', short: number, full: number },
  returns: { label: 'Returns', color: '#C2410C', kind: 'number', short: number, full: number, invert: true },
};

const TrendTooltip = ({ active, payload, metric, weekly, compareLabel }) => {
  if (!active || !payload?.length) return null;
  const bucket = payload[0]?.payload;
  if (!bucket) return null;
  const meta = METRIC_META[metric];
  const current = bucket[metric];
  const previous = bucket[`previous_${metric}`];
  const withYear = bucketYearsDiffer(bucket);
  const previousRange = previousBucketLabel(bucket, weekly) || null;
  return (
    <div className="min-w-[190px] rounded-lg border border-gray-200 bg-white p-3 text-xs shadow-lg">
      <p className="font-semibold text-gray-900">{bucketLabel(bucket, weekly, withYear)}{weekly && bucket.days < 7 ? ` (${bucket.days} days)` : ''}</p>
      <dl className="mt-2 space-y-1 tabular-nums">
        <div className="flex justify-between gap-4"><dt className="text-gray-600">{meta.label}</dt><dd className="font-medium text-gray-900">{meta.full(current)}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-gray-500">{previousRange || 'Comparison'}</dt><dd className="text-gray-700">{meta.full(previous)}</dd></div>
      </dl>
      <div className="mt-2 flex items-center justify-between gap-2">
        <DeltaBadge current={current} previous={previous} kind={meta.kind} invert={meta.invert} size="xs" />
        <span className="text-[11px] text-gray-500">{compareLabel}</span>
      </div>
      <p className="mt-2 text-[11px] text-blue-700">Click to open the daily records</p>
    </div>
  );
};

export const SalesTrendChart = ({ trend = [], metric = 'revenue', onMetric, compareLabel, onSelect, loading = false, height = 280 }) => {
  const weekly = shouldBucketWeekly(trend?.length || 0);
  const buckets = useMemo(() => bucketTrend(trend, { weekly }), [trend, weekly]);
  const byKey = useMemo(() => Object.fromEntries(buckets.map(bucket => [bucket.key, bucket])), [buckets]);
  const meta = METRIC_META[metric] || METRIC_META.revenue;
  const total = buckets.reduce((sum, bucket) => sum + bucket[metric], 0);
  const best = buckets.reduce((top, bucket) => (!top || bucket[metric] > top[metric] ? bucket : top), null);
  const select = state => {
    if (!onSelect || !state) return;
    const byLabel = byKey[state.activeLabel];
    const index = Number(state.activeTooltipIndex);
    const bucket = byLabel || (Number.isInteger(index) ? buckets[index] : null);
    if (bucket) onSelect(bucket);
  };

  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="sales-trend" aria-busy={loading}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">{meta.label} {weekly ? 'by week' : 'by day'}</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            <span className="mr-1 inline-block h-2 w-3 rounded-sm align-middle" style={{ background: meta.color }} aria-hidden="true" />This period
            <span className="ml-3 mr-1 inline-block w-4 border-t-2 border-dashed border-slate-400 align-middle" aria-hidden="true" />{compareLabel ? compareLabel.replace(/^vs /, '') : 'Comparison'}
          </p>
        </div>
        <div role="group" aria-label="Chart metric" className="inline-flex rounded-lg bg-gray-100 p-0.5">
          {Object.entries(METRIC_META).map(([key, item]) => (
            <button key={key} type="button" aria-pressed={metric === key} onClick={() => onMetric?.(key)} data-testid={`sales-trend-${key}`}
              className={`h-7 rounded-md px-2.5 text-[12px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${metric === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`mt-3 ${loading ? 'opacity-60' : ''}`} data-testid="sales-trend-chart" role="img"
        aria-label={`${meta.label} ${weekly ? 'by week' : 'by day'}: ${meta.full(total)} in total${best && best[metric] > 0 ? `, highest ${bucketLabel(best, weekly)} at ${meta.full(best[metric])}` : ''}.`}>
        {!buckets.length && loading ? <Skeleton className="w-full" style={{ height }} /> : (
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={buckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} onClick={select} style={{ cursor: onSelect ? 'pointer' : undefined }}>
              <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#E5E7EB" />
              <XAxis dataKey="key" tickFormatter={value => bucketLabel(byKey[value] || { from: value, to: value }, weekly)} minTickGap={24}
                tick={{ fontSize: 11, fill: '#6B7280' }} axisLine={false} tickLine={false} />
              <YAxis width={58} tickFormatter={value => meta.short(value)} tick={{ fontSize: 10, fill: '#6B7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }} content={<TrendTooltip metric={metric} weekly={weekly} compareLabel={compareLabel} />} />
              <Bar dataKey={metric} name="This period" fill={meta.color} radius={[3, 3, 0, 0]} maxBarSize={36} isAnimationActive={false} />
              <Line dataKey={`previous_${metric}`} name="Comparison" type="monotone" stroke="#94A3B8" strokeWidth={1.75} strokeDasharray="5 4"
                dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {weekly && <p className="mt-2 text-[11px] text-gray-500">Ranges over 45 days are grouped into 7-day blocks from the first day.</p>}
      {onSelect && buckets.length > 0 && (
        <nav aria-label={`Open daily records ${weekly ? 'by week' : 'by day'}`} data-testid="sales-trend-drill">
          <ul className="flex flex-wrap gap-1">
            {buckets.map(bucket => (
              <li key={bucket.key} className="sr-only focus-within:not-sr-only">
                <button type="button" onClick={() => onSelect(bucket)}
                  className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[12px] text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                  Open daily records for {bucketLabel(bucket, weekly)}: {meta.full(bucket[metric])}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </section>
  );
};

export default SalesTrendChart;
